import { Injectable } from '@angular/core';
import { Observable, of, tap, map, switchMap, forkJoin } from 'rxjs';
import { AllergensService } from './allergens.service';
import { MeasurementsService } from './measurements.service';
import { NutritionFactsService } from './nutrition-facts.service';
import { IngredientsService } from './ingredients.service';
import { CategoryService } from './category.service';
import { TagService } from './tag.service';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const TTL_MS = 5 * 60 * 1000; // 5 min default
const LONG_TTL_MS = 30 * 60 * 1000; // 30 min for rarely-changing data

@Injectable({ providedIn: 'root' })
export class SupportDataService {
  private cache = new Map<string, CacheEntry<any>>();

  constructor(
    private allergensSvc: AllergensService,
    private measureSvc: MeasurementsService,
    private nutritionSvc: NutritionFactsService,
    private ingredientsSvc: IngredientsService,
    private categorySvc: CategoryService,
    private tagSvc: TagService,
  ) {}

  private isFresh(key: string, ttl = TTL_MS): boolean {
    const entry = this.cache.get(key);
    return !!entry && (Date.now() - entry.timestamp) < ttl;
  }

  private get<T>(key: string): T | null {
    return this.isFresh(key) ? this.cache.get(key)!.data : null;
  }

  private set(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /** Bust one or more cache keys. Pass a prefix to bust all matching. */
  bust(...keys: string[]): void {
    for (const key of keys) {
      if (key.endsWith('*')) {
        // prefix bust
        const prefix = key.slice(0, -1);
        for (const k of this.cache.keys()) {
          if (k.startsWith(prefix)) this.cache.delete(k);
        }
      } else {
        this.cache.delete(key);
      }
    }
  }

  /** v1.6.611 — Clear the entire cache. Used by AuthService.clearAuth()
   *  on logout / tenant switch so the next user doesn't see the previous
   *  user's cached support data (categories, tags, allergens, nutrition
   *  facts). Without this, switching tenants without a hard refresh
   *  surfaces stale taxonomy from the previous tenant. */
  bustAll(): void { this.cache.clear(); }

  // ── Public cache keys (export for bust calls) ──
  static readonly KEYS = {
    ALLERGENS:      'allergens',
    MEASUREMENTS:   'measurements',
    NUTRITION_FACTS:'nutrition_facts',
    INGREDIENTS:    'ingredients',
    CATEGORIES:     'categories',
    PACKING_SLIP_CATEGORIES: 'packing_slip_categories',
    COOKING_REPORT_CATEGORIES: 'cooking_report_categories',
    TAGS:           'tags',
  };

  // ── Cached accessors ──

  getAllergens(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.ALLERGENS;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.allergensSvc.list({ per_page: 200 }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  getMeasurements(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.MEASUREMENTS;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.measureSvc.list({ per_page: 200 }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  getNutritionFacts(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.NUTRITION_FACTS;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.nutritionSvc.list({ per_page: 200 }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  getIngredients(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.INGREDIENTS;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.ingredientsSvc.list({ per_page: 500 }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  /**
   * Storefront product categories.
   *
   * v1.6.1246 — now scoped to `type: 'products'`. This previously sent NO type, so it
   * returned every category family mixed together. That was tolerable while only
   * `products` and `posts` existed (nothing created posts categories here), but the new
   * `packing_slip` type is the kitchen's operational grouping — the moment an operator
   * creates one it would appear in the product form's storefront category checkboxes
   * and could be attached as a browsing category. Exactly the confusion abdelrhman
   * called out: "never present them as the same field."
   *
   * Sole consumer is the product form (grepped), so scoping is safe.
   */
  getCategories(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.CATEGORIES;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.categorySvc.list({ per_page: 200, type: 'products' }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  /**
   * v1.6.1246 — packing slip categories, the kitchen grouping used by the customer
   * packing slip report. Cached under its OWN key so it can never collide with the
   * storefront list above.
   */
  getPackingSlipCategories(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.PACKING_SLIP_CATEGORIES;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.categorySvc.list({ per_page: 200, type: 'packing_slip' }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  // v1.6.1301 — Asana 1217063026491084: cooking-report categories (type=cooking_report),
  // a separate list from packing-slip categories.
  getCookingReportCategories(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.COOKING_REPORT_CATEGORIES;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.categorySvc.list({ per_page: 200, type: 'cooking_report' }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  getTags(forceRefresh = false): Observable<any[]> {
    const key = SupportDataService.KEYS.TAGS;
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.tagSvc.list({ per_page: 200 }).pipe(
      tap((res: any) => {
        const data = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.set(key, data);
      })
    ) as any;
  }

  /**
   * v1.6.1317 — Product tag picker must show PRODUCT tags only.
   * `getTags()` returns every tag regardless of type; on tenants with lots of blog
   * ("posts") tags that list is dominated by post tags, and a product's own
   * type=products tags can fall outside it — so imported products showed tags with no
   * matching checkbox and they couldn't be de-selected. This fetches type=products with a
   * high per_page and paginates so ALL product tags are present. Cached separately.
   */
  getProductTags(forceRefresh = false): Observable<any[]> {
    const key = 'product_tags';
    const cached = !forceRefresh && this.get<any[]>(key);
    if (cached) return of(cached);

    return this.tagSvc.list({ type: 'products', per_page: 200 }).pipe(
      map((res: any) => {
        const first = res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        const lastPage = res?.data?.last_page ?? 1;
        return { first, lastPage };
      }),
      switchMap(({ first, lastPage }) => {
        if (!lastPage || lastPage <= 1) return of(first);
        // fetch remaining pages and concat
        const rest = [];
        for (let p = 2; p <= lastPage; p++) {
          rest.push(this.tagSvc.list({ type: 'products', per_page: 200, page: p }));
        }
        return forkJoin(rest).pipe(
          map((pages: any[]) => {
            const more = pages.flatMap(r => r?.data?.data ?? r?.data ?? []);
            return [...first, ...more];
          })
        );
      }),
      tap((data: any[]) => this.set(key, data))
    ) as any;
  }
}
